import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { PlusOutlined } from '@ant-design/icons';
import {
  AdditionalFileModal,
  DataTable,
  TravelHealthInsuranceModal,
  AuthorizedButton,
  PageTitle,
  Container,
  SRangePicker,
  SExportButton,
} from '../../../components';
import { tableActions } from '../../../store/reducers/tableReducer';
import styles from './index.module.scss';
import { INITIAL_VALUES } from './const';
import { useAppDispatch, useAppSelector } from '../../../store';
import useHttpService from '../../../services/hook';
import { getColumns } from './columns';
import validationSchema from './validations';
import { IExpandedRow } from '../../../components/DataTable/type';
import { IEnum } from '../../../services/type';
import {
  IFile,
  IWithViewPerm,
  ITravelHealthInsuranceDataSource,
  ITravelHealthInsuranceInitialValues,
} from '../../../types';
import { Checkbox, Flex, Typography } from 'antd';
import { modalTypes } from '../../../assets/enums';
import globalStyles from '../../../assets/styles/index.module.scss';
import { downloadFile } from '../../../helpers/File';
import { cleanupAfterSubmit, prepareModalData } from '../../../utils/functions';
import useFetchParameters from '../../../hooks';
import TravelHealthInsuranceService from '../../../api-clients/TravelHealthInsuranceService';
import EnumService from '../../../api-clients/EnumService';
import AuthService from '../../../api-clients/AuthService';

const TravelHealthInsurance: FC = () => {
  const dispatch = useAppDispatch();
  const executeService = useHttpService();
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );

  // Redux State
  const search = useAppSelector((state) => state.tableReducer.search);
  const sort = useAppSelector((state) => state.tableReducer.sort);
  const page = useAppSelector((state) => state.tableReducer.page);

  // State
  const [selectedItem, setSelectedItem] = useState<
    ITravelHealthInsuranceDataSource | undefined
  >(undefined);
  const [additionalFile, setAdditionalFile] = useState<IFile[] | undefined>(
    undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<
    ITravelHealthInsuranceDataSource[]
  >([]);
  const [isExpired, setIsExpired] = useState<boolean | undefined>(undefined);
  const [dates, setDates] = useState<string[]>([]);
  const [expandedRows, setExpandedRows] = useState<IExpandedRow[]>([]);
  const [statusChoices, setStatusChoices] = useState<IEnum[]>([]);
  const [policyTypes, setPolicyTypes] = useState<IEnum[]>([]);
  const [countries, setCountries] = useState<IEnum[]>([]);
  const [
    travelHealthInsurancePolicyTypes,
    setTravelHealthInsurancePolicyTypes,
  ] = useState<IEnum[]>([]);
  const [additionalFileID, setAdditionalFileID] = useState<number | undefined>(
    undefined,
  );
  const [tempAdditionalFiles, setTempAdditionalFiles] = useState<
    FileList | undefined
  >(undefined);
  const [withViewPerm, setWithViewPerm] = useState<IWithViewPerm[]>([]);

  const parameters = useFetchParameters({
    search,
    sort,
    page,
    dates,
    isExpired,
  });
  const travelHealthInsuranceService = useMemo(
    () => new TravelHealthInsuranceService(executeService, parameters),
    [executeService, parameters],
  );
  const authService = useMemo(
    () => new AuthService(executeService),
    [executeService],
  );
  const enumService = useMemo(
    () => new EnumService(executeService),
    [executeService],
  );

  // Handlers
  const handleFetchWithViewPerm = async () => {
    const response = await authService.fetchWithViewPerm(
      'travelhealthinsurance',
      formik.values.organization,
    );
    if (response && !('statusText' in response)) {
      setWithViewPerm(response.results);
    }
  };

  const handleFetchEnums = async () => {
    const [statusChoices, policyTypes, countries, travelPolicyTypes] =
      await Promise.all([
        enumService.fetchPoliciesStatusChoices(),
        enumService.fetchGroupHealthInsurancePolicyTypes(),
        enumService.fetchAllCountries(),
        enumService.fetchTravelHealthPolicyTypes(),
      ]);

    if (Array.isArray(statusChoices)) {
      setStatusChoices(statusChoices);
    }
    if (Array.isArray(policyTypes)) {
      setPolicyTypes(policyTypes);
    }
    if (Array.isArray(countries)) {
      setCountries(countries);
    }
    if (Array.isArray(travelPolicyTypes)) {
      setTravelHealthInsurancePolicyTypes(travelPolicyTypes);
    }
  };

  const handleFetchData = async () => {
    const response = await travelHealthInsuranceService.fetchData();
    if (response && 'results' in response) {
      dispatch(tableActions.changeTableTotal(response.count));
      const results = response.results.reduce<
        ITravelHealthInsuranceDataSource[]
      >(
        (
          prev: ITravelHealthInsuranceDataSource[],
          cur: ITravelHealthInsuranceDataSource,
        ) => {
          return [...prev, { ...cur, key: cur.id }];
        },
        [],
      );
      setDataSource(results);
      if (additionalFileID) {
        const item = results.find((item) => item.id === additionalFileID);
        if (item) {
          setAdditionalFileID(item.id);
          setAdditionalFile(item.additional_files);
        }
      }
    }
  };

  const handleClickEye = async (id: number) => {
    dispatch(tableActions.changeTableRow(id));
    const expandedRows =
      await travelHealthInsuranceService.fetchExpandedRows(id);
    if (Array.isArray(expandedRows)) {
      setExpandedRows(expandedRows);
    }
  };

  const handleChangeExpired = () => setIsExpired((prev) => !prev);

  const handleOpenModal = () => setIsModalOpen(true);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleOpenEditModal = async (id: number) => {
    const selectedItem = await travelHealthInsuranceService.fetchItemById(id);
    if (selectedItem && 'id' in selectedItem) {
      setSelectedItem(selectedItem);
    }
  };

  const handleCloseEditModal = () => {
    setSelectedItem(undefined);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleUploadFile = (e: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('upload_files', e.target.files);
  };

  const handleClickCancel = async (id: number) => {
    try {
      await travelHealthInsuranceService.cancelPolicy(id);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<ITravelHealthInsuranceDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickDelete = async (id: number) => {
    try {
      await travelHealthInsuranceService.deleteItemById(id);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<ITravelHealthInsuranceDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickExport = async () => {
    const response = await travelHealthInsuranceService.exportData();
    if (response && 'download_url' in response) {
      downloadFile(response.download_url);
    }
  };

  const handleSubmit = async (values: ITravelHealthInsuranceInitialValues) => {
    try {
      await travelHealthInsuranceService.submitForm(values, selectedItem);

      if (selectedItem) {
        await travelHealthInsuranceService.updateStatus(
          selectedItem.id,
          values.recent_status,
          values.email_users || [],
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<ITravelHealthInsuranceDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  const handleSetAdditionalFile = (id: number, file: IFile[]) => {
    setAdditionalFileID(id);
    setAdditionalFile(file);
  };

  const handleUploadAdditionalFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAdditionalFiles(e.target.files);
    }
  };

  const handleCloseAdditionalFile = () => {
    setAdditionalFileID(undefined);
    setAdditionalFile(undefined);
    setTempAdditionalFiles(undefined);
  };

  const handleDeleteFile = async (id: number, fileId: number) => {
    await travelHealthInsuranceService.deleteFile(id, fileId);
    handleFetchData();
  };

  const handleSubmitFile = async () => {
    if (additionalFileID && tempAdditionalFiles) {
      await travelHealthInsuranceService.uploadFiles(
        additionalFileID,
        tempAdditionalFiles,
      );
      setTempAdditionalFiles(undefined);
      handleFetchData();
    }
  };

  // Columns
  const columns = getColumns(
    handleClickEye,
    handleOpenEditModal,
    handleClickDelete,
    handleClickCancel,
    handleSetAdditionalFile,
    organizations,
    statusChoices,
    policyTypes,
    countries,
    travelHealthInsurancePolicyTypes,
  );

  // Form Management
  const formik = useFormik<ITravelHealthInsuranceInitialValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Effects
  useEffect(() => {
    handleFetchEnums();
  }, []);

  useEffect(() => {
    if (formik.values.organization) {
      handleFetchWithViewPerm();
    }
  }, [formik.values.organization]);

  useEffect(() => {
    if (selectedItem) {
      const result = prepareModalData<
        ITravelHealthInsuranceDataSource,
        ITravelHealthInsuranceInitialValues
      >(selectedItem);
      result && formik.setValues(result);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedItem]);

  useEffect(() => {
    handleFetchData();
  }, [isExpired, dates, search, sort.key, sort.order, page.number, page.size]);

  // JSX Return
  return (
    <Container>
      <PageTitle title="Seyahat Sağlık Sigortası" />
      <Flex vertical gap="middle">
        <Flex justify="space-between">
          <Flex vertical gap="small">
            <SRangePicker
              placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
              onChange={(_, dates: [string, string]) => setDates(dates)}
            />
            <Flex gap="small">
              <Checkbox checked={isExpired} onChange={handleChangeExpired} />
              <Typography className={globalStyles.outOfDateText}>
                Sadece Aktif Poliçeleri Göster
              </Typography>
            </Flex>
          </Flex>
          <Flex gap={5}>
            <SExportButton onClick={handleClickExport} />
            <AuthorizedButton
              model="travelhealthinsurance"
              action="add"
              className={styles.button}
              onClick={handleOpenModal}
            >
              <PlusOutlined className={styles.buttonIcon} />
              <div className={styles.buttonText}>Yeni Poliçe Talebi</div>
            </AuthorizedButton>
          </Flex>
        </Flex>
        <DataTable<ITravelHealthInsuranceDataSource>
          scrollable
          columns={columns}
          dataSource={dataSource}
          expandedRows={expandedRows}
          statusChoices={statusChoices}
        />
      </Flex>
      <TravelHealthInsuranceModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Yeni Seyahat Sağlık Sigortası Talebi Oluşturma"
        subTitle="Seyahat Sağlık Sigortası Başvurusu"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
        statusChoices={statusChoices}
        policyTypes={policyTypes}
        countries={countries}
        travelHealthInsurancePolicyTypes={travelHealthInsurancePolicyTypes}
        withViewPerm={withViewPerm}
      />
      <TravelHealthInsuranceModal
        type={modalTypes.EDIT}
        open={!!selectedItem}
        title="Seyahat Sağlık Sigortası Talebi Güncelleme"
        subTitle="Seyahat Sağlık Sigortası Başvuru Güncelleme"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEditModal}
        statusChoices={statusChoices}
        policyTypes={policyTypes}
        countries={countries}
        travelHealthInsurancePolicyTypes={travelHealthInsurancePolicyTypes}
        withViewPerm={withViewPerm}
      />
      <AdditionalFileModal
        open={!!additionalFileID && Array.isArray(additionalFile)}
        model="travelhealthinsuranceadditionalfile"
        tempAdditionalFiles={tempAdditionalFiles}
        additionalFiles={additionalFile}
        handleUploadFile={handleUploadAdditionalFile}
        handleDeleteFile={handleDeleteFile}
        handleSubmit={handleSubmitFile}
        handleCancel={handleCloseAdditionalFile}
      />
    </Container>
  );
};

export default TravelHealthInsurance;
